import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import PaymentGatewayForm, {
	getChannelConfig,
} from '../../../PaymentGatewayForm'
import useMutationCreatePaymentGateway from '@/modules/bank/api/hooks/useMutationCreatePaymentGateway'
import { BankQueryKeys } from '@/modules/bank/keys'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectValue,
	SelectTrigger,
} from '@/common/ui/select'
import useQueryPaymentGatewayProvider from '@/modules/bank/api/hooks/useQueryPaymentGateProvider'
import useQueryGetPaymentGatewayConstraints from '@/modules/bank/api/hooks/useQueryGetPaymentGatewayConstraints'
import { useState, useEffect } from 'react'
import { Label } from '@/common/ui/label'
import LoadingOverlay from '@/common/components/LoadingOverlay'
import Spinner from '@/common/components/Spinner'
import { Checkbox } from '@/common/ui/checkbox'
import type { PaymentChannelTab } from '@/modules/bank/type'
import { CreatePaymentGatewayRequestChannels } from '@/schema'
import { handlePaymentGatewayFormError } from '@/modules/bank/utils/handlePaymentGatewayFormError'

const CHANNEL_OPTIONS: { value: PaymentChannelTab; label: string }[] = [
	{ value: 'QR', label: 'QR' },
	{ value: 'TRANSFER', label: 'Transfer' },
	{ value: 'TRUE_MONEY', label: 'True Money' },
	{ value: 'P2P', label: 'P2P' },
]

export default function CreatePaymentGatewayForm() {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { mutate: createPaymentGateway, isPending: isCreating } =
		useMutationCreatePaymentGateway()
	const { data: constraints, isLoading: isLoadingConstraints } =
		useQueryGetPaymentGatewayConstraints()

	const [paymentGatewayProvider, setPaymentGatewayProvider] = useState<
		string | undefined
	>()
	const [channels, setChannels] = useState<PaymentChannelTab[]>(['QR'])
	const [serverFieldErrors, setServerFieldErrors] = useState<
		Record<string, string>
	>({})

	const { data: paymentGateProviders } = useQueryPaymentGatewayProvider()

	// Default channels by schema (e.g. BADOOPAY -> QR + TRANSFER; TRUE_WALLET -> TRUE_MONEY only)
	const isP2pProvider =
		paymentGatewayProvider === 'MYPAY24' ||
		paymentGatewayProvider === 'AUTOPEER'
	useEffect(() => {
		if (paymentGatewayProvider) {
			if (isP2pProvider) {
				setChannels(['P2P'])
			} else {
				const { defaultChannel } = getChannelConfig(paymentGatewayProvider)
				setChannels(defaultChannel)
			}
		}
	}, [paymentGatewayProvider])

	// Get provider-specific minimum deposit
	const getProviderMinDeposit = (provider: string): number => {
		if (!constraints) return 0
		const providerMin = (
			constraints.providerMinDeposits as Record<string, number>
		)?.[provider]
		return providerMin ?? constraints.defaultMinDeposit ?? 0
	}

	const onSubmit = (values: {
		displayName: string
		bankProviderName: string
		paymentGatewayId: string
		paymentGatewaySecretKey: string
		paymentGatewayPrivateKey: string
		paymentGatewayClientId: string
		paymentGatewayApiKey: string
		paymentGatewayAccountNumber: string
		senderName?: string
		deviceId?: string
		pin?: string
		paymentGatewayAccountName?: string
		paymentGatewayProviderName?: string
		minDepositAmount: number
		maxDepositAmount: number
		enableUploadSlip?: boolean
		uploadSlipDelaySeconds?: number
		isP2p?: boolean
		p2pStep?: number
	}) => {
		if (channels.length === 0) {
			toast.error('กรุณาเลือกอย่างน้อย 1 ช่องทาง')
			return
		}
		setServerFieldErrors({})
		const payload = {
			displayName: values.displayName || '',
			bankProviderName: values.bankProviderName || '',
			paymentGatewayId: values.paymentGatewayId || '',
			paymentGatewaySecretKey: values.paymentGatewaySecretKey || '',
			paymentGatewayPrivateKey: values.paymentGatewayPrivateKey || '',
			paymentGatewayClientId: values.paymentGatewayClientId || '',
			paymentGatewayApiKey: values.paymentGatewayApiKey || '',
			paymentGatewayAccountNumber: values.paymentGatewayAccountNumber,
			senderName: values.senderName || '',
			deviceId: values.deviceId || '',
			pin: values.pin || '',
			paymentGatewayAccountName: values.paymentGatewayAccountName || '',
			paymentGatewayProviderName: values.paymentGatewayProviderName || '',
			minDepositAmount: values.minDepositAmount,
			maxDepositAmount: values.maxDepositAmount,
			channels: channels as CreatePaymentGatewayRequestChannels[],
			enableUploadSlip: values.enableUploadSlip,
			uploadSlipDelaySeconds: values.uploadSlipDelaySeconds,
			isP2p: isP2pProvider ? true : values.isP2p,
			p2pStep: (isP2pProvider ? true : values.isP2p)
				? values.p2pStep
				: undefined,
		}
		createPaymentGateway(
			{ payload },
			{
				onSuccess: () => {
					toast.success('สร้างบัญชีธนาคารออโต้สำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [BankQueryKeys.ChannelCounts],
					})
					navigate('/operator/payment-gateway')
				},
				onError: (error) => {
					const fieldErrors = handlePaymentGatewayFormError(error)
					if (fieldErrors) {
						setServerFieldErrors(fieldErrors)
						toast.error('รบกวนตรวจสอบข้อมูลอีกครั้ง')
					} else {
						showErrorToast(error)
					}
				},
			},
		)
	}

	if (isLoadingConstraints) {
		return <Spinner />
	}

	return (
		<div className="mt-4 space-y-4">
			<LoadingOverlay open={isCreating} message="กำลังสร้าง ธนาคารออโต้..." />
			<Label>ผู้ให้บริการ</Label>
			<Select onValueChange={setPaymentGatewayProvider}>
				<SelectTrigger className="h-8 w-[120px]">
					<SelectValue placeholder="ผู้ให้บริการ" />
				</SelectTrigger>
				<SelectContent>
					{paymentGateProviders?.map((paymentGateProvider) => (
						<SelectItem
							key={paymentGateProvider.name}
							value={paymentGateProvider.name}
							className="flex items-center gap-2"
						>
							{paymentGateProvider.name}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			{paymentGatewayProvider && constraints && (
				<>
					<div className="space-y-2">
						<Label>ช่องทางที่รองรับ (อย่างน้อย 1 ช่องทาง)</Label>
						<div className="flex flex-wrap gap-4">
							{CHANNEL_OPTIONS.map((opt) => {
								const { notAvailableChannel } = getChannelConfig(
									paymentGatewayProvider,
								)
								const disabled = notAvailableChannel.includes(opt.value)
								return (
									<label
										key={opt.value}
										className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
									>
										<Checkbox
											checked={channels.includes(opt.value)}
											disabled={disabled}
											onCheckedChange={(checked) => {
												if (disabled) return
												if (checked) {
													setChannels((prev) => [...prev, opt.value])
												} else {
													setChannels((prev) =>
														prev.filter((c) => c !== opt.value),
													)
												}
											}}
										/>
										<span>{opt.label}</span>
									</label>
								)
							})}
						</div>
					</div>
					<PaymentGatewayForm
						paymentGatewayProvider={paymentGatewayProvider}
						mode="create"
						onSubmit={onSubmit}
						constraints={{
							providerMinDeposit: getProviderMinDeposit(paymentGatewayProvider),
							globalMaxDeposit: constraints.globalMaxDeposit,
						}}
						serverFieldErrors={serverFieldErrors}
					/>
				</>
			)}
		</div>
	)
}
